import { useSearchParams } from 'react-router-dom';
import { useCallback, useMemo } from 'react';

interface UseSearchParamsStateOptions<T> {
  /** Mapping: State-Key → URL-Param-Name (Standard: gleicher Name) */
  paramNames?: Partial<Record<keyof T, string>>;
  /** Standard-Werte (werden nicht in URL geschrieben) */
  defaults: T;
}

/**
 * Synchronisiert ein Filter-Objekt bidirektional mit URL-Search-Params.
 * - Werte gleich dem Default werden aus der URL entfernt (saubere URLs)
 * - Unterstützt string, number und boolean Werte
 * - Gibt [state, setState, resetAll, hasActiveFilters] zurück
 */
export function useSearchParamsState<T extends Record<string, string | number | boolean>>(
  options: UseSearchParamsStateOptions<T>,
): [T, (patch: Partial<T>) => void, () => void, boolean] {
  const { defaults, paramNames } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  const getParamName = useCallback(
    (key: keyof T): string => {
      return (paramNames?.[key] as string) ?? (key as string);
    },
    [paramNames],
  );

  // URL-Params → typisiertes Objekt T parsen
  const state = useMemo(() => {
    const result = { ...defaults };

    for (const key of Object.keys(defaults) as Array<keyof T>) {
      const paramName = getParamName(key);
      const raw = searchParams.get(paramName);

      if (raw === null) continue;

      const defaultValue = defaults[key];
      if (typeof defaultValue === 'boolean') {
        (result as Record<string, unknown>)[key as string] = raw === '1';
      } else if (typeof defaultValue === 'number') {
        const parsed = parseFloat(raw);
        if (!isNaN(parsed)) {
          (result as Record<string, unknown>)[key as string] = parsed;
        }
      } else {
        (result as Record<string, unknown>)[key as string] = raw;
      }
    }

    return result;
  }, [searchParams, defaults, getParamName]);

  // Patch schreibt geänderte Werte in URL-Params
  const setState = useCallback(
    (patch: Partial<T>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);

          for (const [key, value] of Object.entries(patch)) {
            const paramName = getParamName(key as keyof T);
            const defaultValue = defaults[key as keyof T];

            // Gleich dem Default → Param entfernen (saubere URL)
            if (value === defaultValue || value === '' || value === undefined) {
              next.delete(paramName);
            } else if (typeof value === 'boolean') {
              if (value) {
                next.set(paramName, '1');
              } else {
                next.delete(paramName);
              }
            } else {
              next.set(paramName, String(value));
            }
          }

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams, defaults, getParamName],
  );

  // Alle Filter-Params entfernen
  const resetAll = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const key of Object.keys(defaults) as Array<keyof T>) {
          next.delete(getParamName(key));
        }
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams, defaults, getParamName]);

  // Prüfen ob mindestens ein Filter aktiv (nicht-Default) ist
  const hasActiveFilters = useMemo(() => {
    for (const key of Object.keys(defaults) as Array<keyof T>) {
      if (state[key] !== defaults[key]) return true;
    }
    return false;
  }, [state, defaults]);

  return [state, setState, resetAll, hasActiveFilters];
}
